import { Flex, IconButton, Tooltip } from "@chakra-ui/react";
import { BellPlus, CircleHelp } from "lucide-react";
import { AutomacoesNaBarra } from "../AutomacoesDoModulo/AutomacoesDoModuloButton";
import { useAppData, useConfigGuard } from "../../contexts/AppContext";
import { podeVerAlertas } from "../../routes/routeAccess";
import { AccountMenu } from "./AccountMenu";
import { AlertsBell } from "./AlertsBell";
import { GlobalSearch } from "./GlobalSearch";
import { GovbrConnectionIndicator } from "./GovbrConnectionIndicator";
import { Trail } from "./Trail";
import { AUTOMATIONS_PATH, TOPNAV_HEIGHT, TOPNAV_ICON_BUTTON_PROPS } from "./constants";

/** Canal de suporte já usado em outras telas do produto. */
const SUPPORT_MAILTO = "mailto:suporte@taxly.com.br";

// GEN-4080: o destino mora em `constants` (a busca global também o usa); reexportado para quem já importava daqui.
export { AUTOMATIONS_PATH };

/**
 * Barra global do Taxly (GEN-3497, r2).
 *
 * A primeira versão atravessava a tela inteira, branca, entre a sidebar azul e o conteúdo
 * cinza — não pertencia a nenhum dos dois, e ainda tomava o topo da sidebar. Agora ela é
 * uma FAIXA dentro da área de conteúdo: mesmo fundo, sem cor própria, separada só por uma
 * linha. O logo e o botão de recolher voltaram para a sidebar, onde sempre estiveram.
 *
 * À esquerda a trilha, à direita o que é global: busca, automações, alertas, ajuda e conta —
 * **tudo na mesma linha**, como no Notion. Esta faixa já existe para os botões globais, então
 * a trilha não custa altura nenhuma; a página começa direto no seu título (`PageHeader`).
 *
 * Na r6 a trilha desceu para dentro da página, junto do título, e o Thiago reprovou: duas
 * linhas de cabeçalho comiam altura útil em toda tela do sistema. A r7 devolveu a trilha para
 * cá e deixou na página só o título — que é o que faltava no desenho da r2 à r5.
 *
 * Ações de página continuam sendo da página (elas moram na linha do título).
 */
export const TopNav = () => {
  const { requestNavigation } = useConfigGuard();
  const { validation, currentClient } = useAppData();

  // O atalho abre as regras de alerta (GEN-4080: `/automacoes?tipo=alertas`), então a régua é
  // `alerts.read` — a mesma que a rota exige para essa aba (ver `podeAbrirAutomacoes`). Antes o botão não tinha guarda nenhuma e, para
  // quem não podia entrar, o clique só piscava a tela e voltava para o Dashboard; a primeira
  // correção usou `config.read`, que era a permissão que a ROTA exigia. Só que a rota deixou
  // de exigi-la (review r5, para não trancar a troca de senha), e `config.read` é permissão
  // de administrar o CLIENTE — não de mexer em alerta. A busca global segue a mesma régua.
  // GEN-4080 r4: e atrás da liberação de Automações (`podeVerAlertas`) — sem ela o sino com "+" some.
  const canOpenAutomations = podeVerAlertas({ validation, currentClient });

  return (
    <Flex
      as="header"
      role="banner"
      h={TOPNAV_HEIGHT}
      minH={TOPNAV_HEIGHT}
      w="100%"
      flexShrink={0}
      align="center"
      gap="8px"
      // Sem fundo e SEM régua embaixo: a linha de ponta a ponta cortava a tela em duas e
      // encostava na sidebar. O respiro abaixo da faixa (e os cards brancos do conteúdo)
      // já separam — é o que o Notion e o K2 fazem.
      pl="23px"
      pr="30px"
    >
      <Trail />

      <Flex flex="1" minW="8px" />

      <GlobalSearch />

      {/* GEN-3754 — conexão gov.br em segundo plano. Só aparece quando há o que acompanhar. */}
      <GovbrConnectionIndicator />

      {/* GEN-3471 (Thiago, 14/10/2026): o raio de Automações mora AQUI, filtrado pelo módulo da página. */}
      <AutomacoesNaBarra />

      {canOpenAutomations ? (
        // Alertas personalizados com ícone próprio — o raio é de Automações. Sino com "+" escolhido pelo
        // Thiago (14/10/2026): "configurar um alerta", distinto do sino de notificações. GEN-4080: abre a
        // lista de Automações na aba Alertas (o raio, ao lado, continua abrindo pelo módulo da página).
        <Tooltip label="Alertas personalizados" openDelay={600}>
          <IconButton
            aria-label="Alertas personalizados"
            {...TOPNAV_ICON_BUTTON_PROPS}
            icon={<BellPlus size={17} />}
            onClick={() => requestNavigation(AUTOMATIONS_PATH)}
          />
        </Tooltip>
      ) : null}

      <AlertsBell />

      <Tooltip label="Falar com o suporte" openDelay={600}>
        <IconButton
          as="a"
          href={SUPPORT_MAILTO}
          aria-label="Ajuda — falar com o suporte"
          {...TOPNAV_ICON_BUTTON_PROPS}
          display={{ base: "none", md: "inline-flex" }}
          icon={<CircleHelp size={17} />}
        />
      </Tooltip>

      <Flex pl="4px" flexShrink={0}>
        <AccountMenu />
      </Flex>
    </Flex>
  );
};
